/**
 * 🤖 AI-GENERATED COMPONENT
 * Prompt: Render a breadcrumb with interlocking right-pointing arrow segments that wrap to new lines
 * when needed. Hovering a segment highlights it and all preceding segments. Supports both
 * interactive (with links) and non-interactive display modes.
 *
 * API summary:
 * - parts: PathPart[] where PathPart.label is ReactNode; href is required only in interactive mode
 * - isInteractive?: boolean (default true)
 * - variant?: 'normal' | 'contrast'
 */

import cx from 'classnames'
import type { CSSProperties, ElementType } from 'react'
import { useState } from 'react'

import { paperStyles } from '../../libs/paperStyles'
import { isInteractivePathPart, type PathPart } from './types'

type Props = {
  className?: string
  variant?: 'normal' | 'contrast'
  parts: PathPart[]
  showBorder?: boolean
  size?: 'normal' | 'compact'
}

/**
 * Configurable arrow shape constants
 *
 * Adjust these values to customize the appearance of the breadcrumb arrows:
 * - ARROW_ANGLE: Angle of the arrow point in degrees (lower = sharper, higher = wider)
 * - BORDER_RADIUS: Radius of rounded corners on first and last elements (in pixels)
 */
const ARROW_ANGLE = 30 // Angle of the arrow point in degrees
const BORDER_RADIUS = 3 // Border radius for first and last elements in pixels

/**
 * PathBreadcrumb component that displays a navigable path with arrow-shaped parts.
 * Supports wrapping when the path overflows the parent container.
 * Hovering over a part highlights it and all preceding parts (when interactive).
 */
const SIZE_CONFIG = {
  normal: { arrowHeight: 20, edgePadding: 16, innerPadding: 16 },
  compact: { arrowHeight: 14, edgePadding: 8, innerPadding: 8 },
} as const

export const PathBreadcrumb = ({
  parts,
  className,
  variant = 'normal',
  showBorder = true,
  size = 'normal',
}: Props) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const { arrowHeight, edgePadding, innerPadding } = SIZE_CONFIG[size]

  // No custom navigation handling: anchors use native navigation via href

  // Calculate the offset for the arrow tip
  const arrowTipOffset = Math.tan((ARROW_ANGLE * Math.PI) / 180) * arrowHeight

  // Generate clip-path for different item positions
  // Uses CSS variable --arrow-offset for dynamic values
  // Rounded corners are approximated using multiple polygon points
  const getClipPath = (isFirst: boolean, isLast: boolean) => {
    if (isFirst && isLast) {
      // Single item - rounded corners on all sides
      return `polygon(
        ${BORDER_RADIUS}px 0,
        calc(100% - ${BORDER_RADIUS}px) 0,
        100% ${BORDER_RADIUS}px,
        100% calc(100% - ${BORDER_RADIUS}px),
        calc(100% - ${BORDER_RADIUS}px) 100%,
        ${BORDER_RADIUS}px 100%,
        0 calc(100% - ${BORDER_RADIUS}px),
        0 ${BORDER_RADIUS}px
      )`
    }

    if (isFirst) {
      // First item - rounded left corners, arrow right
      return `polygon(
        ${BORDER_RADIUS}px 0,
        calc(100% - var(--arrow-offset)) 0,
        100% 50%,
        calc(100% - var(--arrow-offset)) 100%,
        ${BORDER_RADIUS}px 100%,
        0 calc(100% - ${BORDER_RADIUS}px),
        0 ${BORDER_RADIUS}px
      )`
    }

    if (isLast) {
      // Last item - notch on left, rounded right corners
      return `polygon(
        0 0,
        calc(100% - ${BORDER_RADIUS}px) 0,
        100% ${BORDER_RADIUS}px,
        100% calc(100% - ${BORDER_RADIUS}px),
        calc(100% - ${BORDER_RADIUS}px) 100%,
        0 100%,
        var(--arrow-offset) 50%
      )`
    }

    // Middle items - inward right-pointing notch on left, arrow right
    return `polygon(
      0 0,
      calc(100% - var(--arrow-offset)) 0,
      100% 50%,
      calc(100% - var(--arrow-offset)) 100%,
      0 100%,
      var(--arrow-offset) 50%
    )`
  }

  return (
    <div className={cx('inline-block overflow-hidden', className)}>
      {/* gap-y-2 adds vertical spacing between wrapped lines */}
      <nav
        className={cx(
          'inline-flex flex-wrap items-stretch pr-(--arrow-offset)',
          size === 'compact' ? 'gap-y-1' : 'gap-y-2',
        )}
        style={
          {
            // CSS variables for dynamic arrow dimensions - used throughout child elements
            '--arrow-offset': `${arrowTipOffset}px`,
            '--arrow-offset-overlap': `${-(arrowTipOffset + 2)}px`,
            '--arrow-offset-padding': `${arrowTipOffset + innerPadding}px`,
            '--edge-padding': `${edgePadding}px`,
          } as CSSProperties
        }
      >
        {parts.map((part, index) => {
          const isFirst = index === 0
          const isLast = index === parts.length - 1
          const isHovered = hoveredIndex !== null && index <= hoveredIndex
          const clipPath = getClipPath(isFirst, isLast)
          const isInteractive = isInteractivePathPart(part)

          const hasCustomBackground = !!part.backgroundClassName

          const contentClasses = cx(
            'relative inline-flex items-center outline-0',
            size === 'compact' ? 'py-1' : 'py-2',
            { 'm-px': showBorder },
            {
              'transition duration-200 ease-linear text-inherit!':
                isInteractive,
              // Default (no custom background): highlight with a bg tint on hover/active
              'hover:bg-slate-200 dark:hover:bg-slate-600':
                isInteractive && !hasCustomBackground,
              'bg-slate-200 dark:bg-slate-600':
                isHovered && !hasCustomBackground,
              // Custom background: tweak opacity on hover/active instead of changing bg
              'hover:opacity-80': isInteractive && hasCustomBackground,
              'opacity-80': isHovered && hasCustomBackground,
              'pl-(--edge-padding)': isFirst,
              'pl-(--arrow-offset-padding)': !isFirst,
              'pr-(--edge-padding)': isLast,
              'pr-(--arrow-offset-padding)': !isLast,
            },
            // Only apply paper background when no custom colour is provided
            !hasCustomBackground &&
              {
                normal: paperStyles({ isFaded: false }),
                contrast: paperStyles({ isFaded: true }),
              }[variant],
            part.backgroundClassName,
          )

          const contentStyle = {
            // Using inline style: clip-path not supported by Tailwind
            clipPath,
          } as CSSProperties

          let ElementTypeForPart: ElementType = 'div'

          if (isInteractivePathPart(part) && part.href) {
            ElementTypeForPart = 'a'
          } else if (isInteractivePathPart(part) && part.onClick) {
            ElementTypeForPart = 'button'
          }

          return (
            <div
              // oxlint-disable-next-line react/no-array-index-key
              key={isInteractivePathPart(part) ? (part.href ?? index) : index}
              className={cx('relative flex items-stretch', {
                'mr-(--arrow-offset-overlap)': !isLast,
              })}
            >
              {showBorder ? (
                // Border layer: draws a 1px border around the full arrow shape
                <div
                  className="absolute inset-0 bg-slate-400 dark:bg-slate-600"
                  style={{
                    // Using inline style: clip-path not supported by Tailwind
                    clipPath,
                    // Force consistent rendering to prevent border fading at overlaps
                    imageRendering: 'crisp-edges',
                    WebkitFontSmoothing: 'antialiased',
                    backfaceVisibility: 'hidden',
                  }}
                />
              ) : (
                // Arrow separator – draws only the > chevron outline between parts
                !isFirst && (
                  <div
                    className="absolute left-0 top-0 bottom-0 bg-slate-400 dark:bg-slate-600"
                    style={{
                      width: 'var(--arrow-offset)',
                      clipPath:
                        'polygon(0 0, 1px 0, 100% 50%, 1px 100%, 0 100%, calc(100% - 1px) 50%)',
                    }}
                  />
                )
              )}
              <ElementTypeForPart
                className={contentClasses}
                style={contentStyle}
                {...(isInteractivePathPart(part)
                  ? {
                      onMouseEnter: () => setHoveredIndex(index),
                      onMouseLeave: () => setHoveredIndex(null),
                      ...(part.href ? { href: part.href } : {}),
                      ...(part.onClick ? { onClick: part.onClick } : {}),
                    }
                  : {})}
              >
                <span
                  className={cx(
                    'relative whitespace-nowrap',
                    size === 'compact' && 'text-sm',
                    part.textClassName,
                  )}
                >
                  {part.label}
                </span>
              </ElementTypeForPart>
            </div>
          )
        })}
      </nav>
    </div>
  )
}
